import React from "react";
import { motion } from "framer-motion";
import AnimatedGrid from "../components/ui/AnimatedGrid";

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      title: "Celebrating Dashain: Connecting Communities Across Continents",
      excerpt:
        "Discover how our recent Dashain celebration brought together over 500 community members in a beautiful display of tradition and unity.",
      date: "March 15, 2024",
      author: "3E Entertainment Team",
      category: "Cultural Events",
      accentColor: "#C72631",
    },
    {
      id: 2,
      title:
        "The Art of Traditional Nepali Dance: Preserving Heritage Through Movement",
      excerpt:
        "Explore the rich history and cultural significance of traditional Nepali dances that continue to captivate audiences worldwide.",
      date: "March 10, 2024",
      author: "Cultural Committee",
      category: "Culture & Arts",
      accentColor: "#C72631",
    },
    {
      id: 3,
      title:
        "Building Bridges: How Cultural Events Strengthen Diaspora Communities",
      excerpt:
        "Learn about the important role cultural events play in maintaining connections within the global Nepali diaspora.",
      date: "March 5, 2024",
      author: "Community Relations",
      category: "Community",
      accentColor: "#C72631",
    },
    {
      id: 4,
      title: "From Nepal to the World: Stories of Cultural Ambassadors",
      excerpt:
        "Meet the inspiring individuals who are spreading Nepali culture and traditions across different continents.",
      date: "February 28, 2024",
      author: "3E Entertainment Team",
      category: "Community Stories",
      accentColor: "#C72631",
    },
    {
      id: 5,
      title: "The Evolution of Nepali Festivals in Modern Times",
      excerpt:
        "How traditional festivals are adapting to contemporary settings while maintaining their cultural essence.",
      date: "February 20, 2024",
      author: "Cultural Committee",
      category: "Cultural Events",
      accentColor: "#C72631",
    },
    {
      id: 6,
      title:
        "Music That Moves Mountains: Contemporary Nepali Artists Making Waves",
      excerpt:
        "Spotlight on talented Nepali musicians who are creating beautiful fusion of traditional and modern sounds.",
      date: "February 15, 2024",
      author: "Arts & Culture",
      category: "Culture & Arts",
      accentColor: "#C72631",
    },
  ];

  const categories = [
    "All",
    "Cultural Events",
    "Culture & Arts",
    "Community",
    "Community Stories",
  ];
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredPosts =
    selectedCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="hero-overlay" />
        <div className="hero-pattern" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Our Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Stories, insights, and cultural celebrations from our community
          </motion.p>
        </motion.div>
      </section>

      {/* Filter Categories */}
      <section className="filter-section">
        <div className="filter-container">
          <motion.div
            className="filter-buttons"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`filter-btn ${selectedCategory === category ? "active" : ""
                  }`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="blog-grid-section">
        <div className="blog-container">
          <AnimatedGrid
            direction="right"
            columns="repeat(auto-fit, minmax(350px, 1fr))"
            gap="30px"
          >
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div
                  className="card-accent"
                  style={{ background: post.accentColor }}
                />
                <div className="card-content">
                  <div className="card-meta">
                    <span className="category">{post.category}</span>
                    <span className="date">{post.date}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="card-footer">
                    <span className="author">By {post.author}</span>
                    <a href={`/blog/${post.id}`} className="read-more">
                      Read More
                      <span className="arrow">→</span>
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatedGrid>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <div className="newsletter-overlay" />
        <motion.div
          className="newsletter-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Stay Updated with Our Latest Stories
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Subscribe to our newsletter and never miss our cultural insights,
            event updates, and community stories.
          </motion.p>
          <motion.div
            className="newsletter-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <input
              type="email"
              placeholder="Enter your email address"
            />
            <button type="submit" className="subscribe-btn">
              Subscribe
              <span className="btn-arrow">→</span>
            </button>
          </motion.div>
        </motion.div>
      </section>

      <style>{`
        .blog-page {
          overflow-x: hidden;
        }

        /* Hero Section */
        .blog-hero {
          position: relative;
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-pattern {
          display: none;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
        }

        /* Filter Section */
        .filter-section {
          background: #fff;
          padding: 2rem 2rem 0;
          position: relative;
          z-index: 10;
          margin-top: -40px;
        }

        .filter-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .filter-buttons {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
          background: white;
          padding: 1.5rem 2rem;
          border-radius: 16px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
        }

        .filter-btn {
          background: #f7f7f7;
          border: 2px solid transparent;
          color: #666;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .filter-btn:hover {
          background: #fff;
          border-color: #C72631;
          color: #C72631;
        }

        .filter-btn.active {
          background: #C72631;
          border-color: #C72631;
          color: white;
        }

        /* Blog Grid Section */
        .blog-grid-section {
          padding: 4rem 2rem 6rem;
          background: #fff;
        }

        .blog-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .blog-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          border: 1px solid #f0f0f0;
          transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative;
        }

        .blog-card:hover {
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
          border-color: transparent;
        }

        .card-accent {
          height: 4px;
          width: 100%;
        }

        .card-content {
          padding: 2rem;
        }

        .card-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .category {
          background: rgba(199, 38, 49, 0.08);
          color: #C72631;
          padding: 0.35rem 0.85rem;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .date {
          color: var(--text-muted-light, #6e6e6e);
          font-size: 0.85rem;
        }

        .blog-card h3 {
          font-size: 1.25rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1rem;
          line-height: 1.4;
        }

        .blog-card p {
          color: #666;
          font-size: 0.95rem;
          line-height: 1.7;
          margin: 0 0 1.5rem;
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1.5rem;
          border-top: 1px solid #f0f0f0;
        }

        .author {
          color: var(--text-muted-light, #6e6e6e);
          font-size: 0.85rem;
        }

        .read-more {
          color: #C72631;
          font-weight: 600;
          font-size: 0.9rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .read-more:hover {
          color: #a01f28;
        }

        .read-more .arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .read-more:hover .arrow {
          transform: translateX(5px);
        }

        /* Newsletter Section */
        .newsletter-section {
          position: relative;
          padding: 6rem 2rem;
          background: url("https://images.unsplash.com/photo-1539650116574-75c0c6d0fbdf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80") center/cover no-repeat;
          text-align: center;
        }

        .newsletter-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .newsletter-content {
          position: relative;
          z-index: 2;
          max-width: 600px;
          margin: 0 auto;
        }

        .newsletter-content h2 {
          font-size: clamp(1.8rem, 4vw, 2.5rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1rem;
        }

        .newsletter-content p {
          font-size: 1.05rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          margin: 0 0 2rem;
        }

        .newsletter-form {
          display: flex;
          gap: 1rem;
          max-width: 500px;
          margin: 0 auto;
        }

        .newsletter-form input {
          flex: 1;
          padding: 1rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          outline: none;
        }

        .subscribe-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          color: #C72631;
          padding: 1rem 1.75rem;
          border: none;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          white-space: nowrap;
        }

        .subscribe-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .subscribe-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .blog-hero {
            min-height: 50vh;
          }

          .filter-buttons {
            gap: 0.75rem;
            padding: 1rem;
          }

          .filter-btn {
            padding: 0.6rem 1rem;
            font-size: 0.85rem;
          }

          .blog-grid-section {
            padding: 3rem 1.5rem 4rem;
          }

          .newsletter-form {
            flex-direction: column;
          }

          .newsletter-form input,
          .subscribe-btn {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .hero-content h1 {
            font-size: 2rem;
          }

          .filter-btn {
            padding: 0.5rem 0.85rem;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Blog;
